import { Button, Input, Select, Table, message } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Agreement } from '../../../features/agreement/agreementSlice';
import { numberWithCommas } from '../../../helpers/number';
import { getPendingEStampAgreementsThunk } from '../../../services/agreementService/agreementThunk';
import agreementService from '../../../services/agreementService/agreementService';
import { AppDispatch, RootState } from '../../../store/store';
import { onCalculateNetDisbursementAmount } from '../../../utils/calculation.util';
import { displayDate } from '../../../utils/datetime.util';

import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/es/table';
import { getPendingSmeEStampListingThunk } from '../../../services/smeAgreementService/smeAgreementThunk';

const { Search } = Input;

const PendingEStampTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { pendingSmeEStampList } = useSelector((state: RootState) => state.smeAgreement);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    [pageNum, pageSize]
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('agreementNo');
  const [searchValue, setSearchValue] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedAgreementIds, setSelectedAgreementIds] = useState<string[]>([]);
  const [isGeneratingXml, setIsGeneratingXml] = useState(false);

  const resetParams = (type: string) => {
    setSearchFiled(type);
    initialParams.pagination.current = 1;
    initialParams.pagination.pageSize = 10;
    setPaginationState(initialParams.pagination);
  };

  const tablePagination = {
    total: pendingSmeEStampList?.data?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      sorter: (a, b) => a.signDate - b.signDate,
      sortOrder: sortedInfo.columnKey === 'signDate' ? sortedInfo.order : null,
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            queryParams.set('from', 'estamp-pending');
            const newSearch = queryParams.toString();

            return navigate({
              pathname: `/sme/agreement/details/${record.smeAgreementId}`,
              search: newSearch,
            });
          }}
        >
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Organization Type',
      dataIndex: 'organizationType',
      key: 'organizationType',
      render: (text, record) => record?.organizationType ?? '-',
    },
    {
      title: 'Name',
      dataIndex: 'applicantName',
      key: 'applicantName',
      render: (text, record) => record?.applicantName ?? '-',
    },

    {
      title: 'NRIC',
      dataIndex: 'applicantNric',
      key: 'applicantNric',
      render: (text, record) => record?.applicantNric ?? '-',
    },
    {
      title: 'Finance Amount',
      key: 'financeAmount',
      render: (text, record) =>
        record?.financeAmount != null ? `RM ${numberWithCommas(record?.financeAmount)}` : '-',
    },
  ];

  const handleGenerateXml = async () => {
    if (selectedAgreementIds.length === 0) {
      message.warning('Please select at least one agreement.');
      return;
    }
    setIsGeneratingXml(true);
    try {
      const blob = await agreementService.downloadLhdnBulkStampingXml(selectedAgreementIds);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `LHDN_Bulk_Stamping_${new Date().toISOString().slice(0, 10)}.xml`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSelectedRowKeys([]);
      setSelectedAgreementIds([]);
      message.success('XML generated successfully.');
      fetchAgreements(initialParams);
    } catch (error: any) {
      message.error(error?.message || 'Failed to generate XML.');
    } finally {
      setIsGeneratingXml(false);
    }
  };

  const fetchAgreements = async (params?: any) => {
    await dispatch(
      getPendingSmeEStampListingThunk({
        query: params,
        status: 'pending',
      })
    );
  };

  const onSearch = (value: string) => {
    value = value.trim();
    if (value) {
      setSearchValue(value.toUpperCase());
      fetchAgreements({
        ...initialParams,
        filters: { ...filterState },
        search: {
          [searchFiled]: value.toUpperCase(),
        },
      });
      // setFilterState({ ...initialParams.filters, [searchFiled]: value });
    } else {
      fetchAgreements({
        ...initialParams,
        filters: { ...filterState },
      });
    }
  };

  const handleTableChange: TableProps<any>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    setSortedInfo(sorter as SorterResult<any>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });

    const param = {
      ...initialParams,
      ...filterState,
      pagination,
      sortField: sorter?.order ? sorter?.columnKey : 'createdAt',
      sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
      ...(searchValue.length > 0 && {
        search: {
          [searchFiled]: searchValue.toUpperCase(),
        },
      }),
    };

    fetchAgreements(param);

    queryParams.set('pageNum', `${pagination.current ?? 1}`);
    queryParams.set('pageSize', `${pagination.pageSize ?? 10}`);
    const newSearch = queryParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  return (
    <>
      <Helmet>
        <title>Pending Stamp Certificate - redCASH CEP</title>
      </Helmet>
      <Input.Group compact>
        <Select
          defaultValue="agreementNo"
          style={{ width: 200 }}
          onChange={(value) => resetParams(value)}
          options={[
            { value: 'applicationNo', label: 'Application No.' },
            { value: 'agreementNo', label: 'Agreement No.' },
            { value: 'name', label: 'Applicant Name' },
            { value: 'nric', label: 'Applicant NRIC' },
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Button
          type="primary"
          onClick={handleGenerateXml}
          disabled={selectedAgreementIds.length === 0}
          loading={isGeneratingXml}
        >
          Generate XML ({selectedAgreementIds.length})
        </Button>
        {selectedAgreementIds.length > 0 && (
          <Button
            onClick={() => {
              setSelectedRowKeys([]);
              setSelectedAgreementIds([]);
            }}
          >
            Clear Selection
          </Button>
        )}
      </div>
      <Table
        rowKey="smeAgreementId"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={pendingSmeEStampList?.data.data ?? []}
        loading={pendingSmeEStampList?.isLoading}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedAgreementIds(rows.map((r: any) => r.smeAgreementId));
          },
        }}
      />
    </>
  );
};

export default PendingEStampTab;
